import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Card, Form, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Page } from "@zhihaoscm/entity";
import type { PortWharfBerthPageParams, PortWharfBerthVo } from "@/entity/logistics/portWharfBerth";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { confirmDialog } from "@/utils/confirmDialog";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import {
    HANDLING_ATTRIBUTE,
    PORT_BERTH_TYPE,
    PORT_WHARF_BERTH_GOODS_TYPE,
    PORT_WHARF_BERTH_TYPE
} from "@zhihaoscm/meta";
import { BerthLocationModal } from "./BerthLocationModal";
import { useUserStore } from "@/stores/react/authStore";

export interface WharfManagementQuery extends Omit<PortWharfBerthPageParams, "type"> {
    page: number;
    size: number;
    type: number;
    portBerthType: number[];
    handlingAttribute: number[];
    goodsType: number[];
}

export interface WharfManagementPageProps {
    loadWharves?: (query: WharfManagementQuery) => Promise<Page<PortWharfBerthVo>>;
    deleteWharf?: (id: number, type: number) => Promise<unknown>;
    permissions?: string[];
}

const defaultQuery: WharfManagementQuery = {
    page: 1,
    size: 10,
    type: PORT_WHARF_BERTH_TYPE.PORT.code,
    portBerthType: PORT_BERTH_TYPE.options.map(option => Number(option.value)),
    handlingAttribute: HANDLING_ATTRIBUTE.options.map(option => Number(option.value)),
    goodsType: PORT_WHARF_BERTH_GOODS_TYPE.options.map(option => Number(option.value))
};

/** 复用 Vue 码头管理列表的 `/port/paging` 接口。 */
export async function loadWharfManagement(query: WharfManagementQuery): Promise<Page<PortWharfBerthVo>> {
    const response = await shippingRequestHttp.service.get<Page<PortWharfBerthVo>>("/port/paging", { params: query });
    return response.data;
}

/** 复用 Vue 码头删除接口，沿用 type=1 的码头数据契约。 */
export async function deleteWharfManagement(id: number, type: number) {
    const response = await shippingRequestHttp.service.delete(`/port/${id}`, { params: { id, type } });
    return response.data;
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatContact = (row: PortWharfBerthVo) => {
    const name = row.contactName?.trim();
    const phone = row.phone?.trim();
    if (!name && !phone) return DEFAULT_VALUE;
    if (name && phone) return `${name} ${phone}`;
    return name || phone || DEFAULT_VALUE;
};

/** 将 zhihao 表格的未知单元格值收敛为 React 可渲染的码头字段。 */
const formatCellValue = (value: unknown) =>
    typeof value === "string" || typeof value === "number" ? value : DEFAULT_VALUE;

/** 物流管理-码头管理列表，保留 Vue 首屏筛选、枚举、字段和分页契约。 */
export function WharfManagementPage({
    loadWharves = loadWharfManagement,
    deleteWharf = deleteWharfManagement,
    permissions
}: WharfManagementPageProps) {
    const storePermissions = useUserStore(user => user.userInfo.permissions || []);
    const banButton = useUserStore(user => user.banButton);
    // 保持 Vue v-auth 语义：显式测试权限优先，否则读取当前登录用户权限。
    const canRead = (permissions ?? storePermissions).includes("ROLE_TERMINAL_R");
    const canDeal = (permissions ?? storePermissions).includes("ROLE_TERMINAL_DEAL");
    const canDelete = (permissions ?? storePermissions).includes("ROLE_TERMINAL_W");
    const [rows, setRows] = useState<PortWharfBerthVo[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<WharfManagementQuery>(defaultQuery);
    const [keyword, setKeyword] = useState("");
    const [harborName, setHarborName] = useState("");
    const [portBerthType, setPortBerthType] = useState(defaultQuery.portBerthType);
    const [handlingAttribute, setHandlingAttribute] = useState(defaultQuery.handlingAttribute);
    const [goodsType, setGoodsType] = useState(defaultQuery.goodsType);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadWharves(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("码头列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadWharves, query]);

    // 将 Vue ProTable 的筛选草稿组装成码头分页接口参数，并归一到第一页。
    const submitFilters = () => {
        const nextQuery: WharfManagementQuery = {
            ...defaultQuery,
            page: 1,
            portBerthType,
            handlingAttribute,
            goodsType
        };
        if (keyword.trim()) nextQuery.keyword = keyword.trim();
        if (harborName.trim()) nextQuery.harborName = harborName.trim();
        setQuery(nextQuery);
    };

    const resetFilters = () => {
        setKeyword("");
        setHarborName("");
        setPortBerthType(defaultQuery.portBerthType);
        setHandlingAttribute(defaultQuery.handlingAttribute);
        setGoodsType(defaultQuery.goodsType);
        setQuery(defaultQuery);
    };

    // 删除成功后沿用 Vue 列表刷新行为，保留当前筛选和分页参数。
    const handleDelete = useCallback(
        async (id: number) => {
            if (!(await confirmDialog("是否删除码头？"))) return;
            try {
                await deleteWharf(id, PORT_WHARF_BERTH_TYPE.PORT.code);
                notifyOperationSuccess("已删除");
                const result = await loadWharves(query);
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            } catch {
                setErrorMessage("码头删除失败");
            }
        },
        [deleteWharf, loadWharves, query]
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 码头 ZhTable 的字段、排序和 operation 插槽。
    const wharfColumns = useMemo<TableColumnProps<PortWharfBerthVo>[]>(
        () => [
            {
                key: "index",
                label: "编号",
                width: 60,
                prop: "id",
                render: ({ row, rowIndex }) => (
                    <Link to={`/logistics/portWharfBerth/wharfDetail?id=${row.id}`}>
                        {(query.page - 1) * query.size + rowIndex + 1}
                    </Link>
                )
            },
            {
                key: "name",
                label: "名称",
                minWidth: 100,
                prop: "name",
                render: ({ row, cellValue }) =>
                    canRead ? (
                        <Link to={`/logistics/portWharfBerth/wharfDetail?id=${row.id}`}>
                            {formatCellValue(cellValue)}
                        </Link>
                    ) : (
                        formatCellValue(cellValue)
                    )
            },
            {
                key: "shortName",
                label: "简称",
                minWidth: 100,
                prop: "shortName",
                render: ({ cellValue }) => formatCellValue(cellValue)
            },
            {
                key: "harborShortName",
                label: "所属港口",
                minWidth: 100,
                prop: "harborShortName",
                render: ({ cellValue }) => formatCellValue(cellValue)
            },
            {
                key: "fullAddress",
                label: "位置",
                minWidth: 200,
                prop: "fullAddress",
                render: ({ cellValue }) => formatCellValue(cellValue)
            },
            {
                key: "portBerthType",
                label: "码头类型",
                prop: "portBerthType",
                render: ({ row }) => PORT_BERTH_TYPE.getIntroByCode(row.portBerthType) || DEFAULT_VALUE
            },
            {
                key: "handlingAttribute",
                label: "装卸属性",
                minWidth: 60,
                prop: "handlingAttribute",
                render: ({ row }) => HANDLING_ATTRIBUTE.getIntroByCode(row.handlingAttribute) || DEFAULT_VALUE
            },
            {
                key: "goodsType",
                label: "货物类型",
                prop: "goodsType",
                render: ({ row }) => PORT_WHARF_BERTH_GOODS_TYPE.getIntroByCode(row.goodsType) || DEFAULT_VALUE
            },
            {
                key: "contact",
                label: "联系人信息",
                minWidth: 100,
                prop: "contactName",
                render: ({ row }) => formatContact(row)
            },
            {
                key: "updatedTime",
                label: "更新日期",
                minWidth: 100,
                prop: "updatedTime",
                sortable: true,
                render: ({ cellValue }) => formatDate(typeof cellValue === "string" ? cellValue : undefined)
            },
            {
                key: "operation",
                label: "操作",
                minWidth: 213,
                prop: "operation",
                fixed: "right",
                render: ({ row }) => (
                    <Space>
                        {canDeal ? (
                            banButton ? (
                                <Button disabled type="link">
                                    修改
                                </Button>
                            ) : (
                                <Link to={`/logistics/portWharfBerth/wharfEdit?id=${row.id}`}>修改</Link>
                            )
                        ) : null}
                        {canRead ? <BerthLocationModal berth={row} disabled={banButton} /> : null}
                        {canDelete ? (
                            <Button disabled={banButton} onClick={() => void handleDelete(Number(row.id))}>
                                删除
                            </Button>
                        ) : null}
                    </Space>
                )
            }
        ],
        [banButton, canDeal, canDelete, canRead, handleDelete, query.page, query.size]
    );

    // 保留 Vue 码头列表对更新时间的 sortKey/sortOrder 请求契约。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "updatedTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "updated_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    return (
        <section aria-label="wharf-management-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<PortWharfBerthVo>
                    rowKey="id"
                    data={rows}
                    columns={normalizeOperationColumns(wharfColumns, rows)}
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4" wrap>
                                <Typography.Title className="mb-0 mt-0" level={4}>
                                    码头管理
                                </Typography.Title>
                                {canDeal ? (
                                    banButton ? (
                                        <Button disabled type="link">
                                            新增
                                        </Button>
                                    ) : (
                                        <Link to="/logistics/portWharfBerth/wharfAdd">新增</Link>
                                    )
                                ) : null}
                            </Space>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form className="admin-table-filter-form mb-4" layout="inline" onFinish={submitFilters}>
                                <Form.Item label="码头名称/简称">
                                    <Input
                                        aria-label="码头名称/简称"
                                        placeholder="请输入码头名称/简称关键词搜索"
                                        value={keyword}
                                        onChange={event => setKeyword(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item label="所属港口简称">
                                    <Input
                                        aria-label="所属港口简称"
                                        placeholder="请输入所属港口简称搜索"
                                        value={harborName}
                                        onChange={event => setHarborName(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item label="码头类型">
                                    <Select
                                        aria-label="码头类型"
                                        maxTagCount={1}
                                        mode="multiple"
                                        placeholder="请选择码头类型"
                                        value={portBerthType}
                                        options={PORT_BERTH_TYPE.options}
                                        onChange={values => setPortBerthType(values.map(Number))}
                                    />
                                </Form.Item>
                                <Form.Item label="装卸属性">
                                    <Select
                                        aria-label="装卸属性"
                                        maxTagCount={1}
                                        mode="multiple"
                                        placeholder="请选择装卸属性"
                                        value={handlingAttribute}
                                        options={HANDLING_ATTRIBUTE.options}
                                        onChange={values => setHandlingAttribute(values.map(Number))}
                                    />
                                </Form.Item>
                                <Form.Item label="货物类型">
                                    <Select
                                        aria-label="货物类型"
                                        maxTagCount={1}
                                        mode="multiple"
                                        placeholder="请选择货物类型"
                                        value={goodsType}
                                        options={PORT_WHARF_BERTH_GOODS_TYPE.options}
                                        onChange={values => setGoodsType(values.map(Number))}
                                    />
                                </Form.Item>
                                <Space>
                                    <Button aria-label="查询" htmlType="submit" type="primary">
                                        查询
                                    </Button>
                                    <Button onClick={resetFilters}>重置</Button>
                                </Space>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}
